import { useTranslation } from 'react-i18next';

import { Button } from './Button';
import { cx } from './cx';

/**
 * Which rows of a long list are on screen, and the way to the next ones: `1–50 of 312`,
 * then Previous and Next.
 *
 * For a list the server pages. The page shown is the one the answer says it gave, not the one
 * last asked for: a server clamps a page past the end to the last one — the list shrank under a
 * write — and the pager then simply follows.
 *
 * Nothing when everything fits on one page.
 *
 * @param page The page on screen, from 0.
 * @param count Formats a number the firm's way, as `useTenantFormats().count` does.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
  count = String,
  disabled = false,
  className,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  count?: (value: number) => string;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  if (total <= pageSize || pageSize <= 0) return null;

  const first = page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);

  return (
    <div className={cx('flex items-center justify-end gap-2', className)}>
      <span className="text-secondary text-ink-2">
        {t('pager.range', { first: count(first), last: count(last), total: count(total) })}
      </span>
      <Button size="sm" disabled={disabled || page === 0} onClick={() => onPage(page - 1)}>
        {t('pager.previous')}
      </Button>
      <Button size="sm" disabled={disabled || last >= total} onClick={() => onPage(page + 1)}>
        {t('pager.next')}
      </Button>
    </div>
  );
}
